import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import api from '../../services/api';
import { getUploadUrl } from '../../utils/helpers';

/**
 * AdminPhotos Component - Professional Photo Moderation Interface
 *
 * Features:
 * - Data table layout for pending photos
 * - User details, photo type, upload time
 * - Quick approve/reject actions
 * - Detailed modal review with large photo view
 * - Professional styling with hover effects
 */

const AdminPhotos = () => {
  const [pendingPhotos, setPendingPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [reviewNotes, setReviewNotes] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    fetchPendingPhotos();
  }, []);

  const fetchPendingPhotos = async () => {
    try {
      setLoading(true);
      const response = await api.get('/admin/photos/pending');

      // API interceptor response.data döndürüyor, bu yüzden doğrudan response.photos'a erişiyoruz
      if (response && response.photos && Array.isArray(response.photos)) {
        setPendingPhotos(response.photos);
      } else {
        console.error('Invalid response format - expected {success: true, photos: Array}', response);
        setPendingPhotos([]);
        toast.error('Invalid response format from server');
      }
    } catch (error) {
      console.error('Error fetching pending photos:', error);
      console.error('Error details:', error.response?.data);
      toast.error('Failed to load pending photos');
      setPendingPhotos([]);
    } finally {
      setLoading(false);
    }
  };

  const handleReview = async (photoId, action) => {
    if (action === 'reject' && !reviewNotes.trim()) {
      toast.error('Please provide a reason for rejection');
      return;
    }

    setActionLoading(true);
    try {
      console.log('🔄 Sending review request:', { photoId, action, notes: reviewNotes });

      const response = await api.put(`/admin/photos/${photoId}`, {
        action,
        notes: action === 'reject' ? reviewNotes : null
      });

      console.log('📥 Review response:', response);

      // API interceptor response.data döndürüyor
      if (response && response.success) {
        toast.success(`Photo ${action}d successfully`);
        setSelectedPhoto(null);
        setReviewNotes('');
        fetchPendingPhotos(); // Refresh the list
      } else {
        throw new Error(response?.message || 'Unknown error');
      }
    } catch (error) {
      console.error('Error reviewing photo:', error);
      console.error('Error response:', error.response?.data);

      // More specific error messages
      let errorMessage = 'Failed to review photo';
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }

      toast.error(errorMessage);
    } finally {
      setActionLoading(false);
    }
  };

  const getPhotoTypeLabel = (type) => {
    switch (type) {
      case 'profile': return 'Profile Photo';
      case 'normal': return 'Public Photo';
      case 'private': return 'Private Photo';
      case 'cover': return 'Cover Photo';
      default: return type;
    }
  };

  const formatDate = (timestamp) => {
    try {
      if (!timestamp) return 'Unknown';

      // Backend'den gelen timestamp Unix timestamp (saniye cinsinden)
      const date = new Date(timestamp * 1000);

      if (isNaN(date.getTime())) {
        console.warn('Invalid timestamp:', timestamp);
        return 'Invalid Date';
      }

      // Türkçe format
      return date.toLocaleString('tr-TR', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      console.error('Date formatting error:', error, timestamp);
      return 'Invalid Date';
    }
  };

  if (loading) {
    return (
      <div style={{
        padding: '40px',
        textAlign: 'center'
      }}>
        <div style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '12px',
          padding: '20px 32px',
          background: 'white',
          borderRadius: '8px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
        }}>
          <div style={{
            width: '20px',
            height: '20px',
            border: '2px solid #e9ecef',
            borderTop: '2px solid #007bff',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite'
          }}></div>
          <span style={{
            fontSize: '16px',
            color: '#495057',
            fontWeight: '500'
          }}>Loading pending photos...</span>
        </div>
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  return (
    <div style={{ padding: '20px' }}>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '30px',
        borderBottom: '1px solid #eee',
        paddingBottom: '15px'
      }}>
        <h2 style={{ margin: '0', color: '#333' }}>Photo Moderation</h2>
        <div style={{ display: 'flex', gap: '20px' }}>
          <span style={{ fontSize: '14px', color: '#666' }}>
            <strong>{pendingPhotos.length}</strong> pending photos
          </span>
        </div>
      </div>

      <div style={{
        background: 'white',
        borderRadius: '8px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        overflow: 'hidden'
      }}>
        {pendingPhotos.length === 0 ? (
          <div style={{
            textAlign: 'center',
            padding: '60px',
            color: '#666',
            fontSize: '16px'
          }}>
            <div style={{ fontSize: '48px', marginBottom: '16px', color: '#ddd' }}>📷</div>
            <p>No pending photos to review</p>
            <p style={{ fontSize: '14px', color: '#999', marginTop: '8px' }}>
              Photos uploaded by users will appear here for moderation
            </p>
          </div>
        ) : (
          <div>
            {/* Table Header */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: '60px 1fr 120px 140px 180px',
              gap: '16px',
              padding: '16px 20px',
              background: '#f8f9fa',
              borderBottom: '1px solid #dee2e6',
              fontSize: '14px',
              fontWeight: '600',
              color: '#495057'
            }}>
              <div>Photo</div>
              <div>User Details</div>
              <div>Type</div>
              <div>Uploaded</div>
              <div style={{ textAlign: 'center' }}>Actions</div>
            </div>

            {/* Table Body */}
            {pendingPhotos.map((photo, index) => (
              <div
                key={photo.id}
                style={{
                  display: 'grid',
                  gridTemplateColumns: '60px 1fr 120px 140px 180px',
                  gap: '16px',
                  padding: '16px 20px',
                  borderBottom: index < pendingPhotos.length - 1 ? '1px solid #dee2e6' : 'none',
                  alignItems: 'center',
                  transition: 'background-color 0.15s ease',
                  cursor: 'pointer'
                }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f8f9fa'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
              >
                {/* Photo Thumbnail */}
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <img
                    src={getUploadUrl(photo.path)}
                    alt={`${photo.full_name}'s photo`}
                    onClick={() => setSelectedPhoto(photo)}
                    style={{
                      width: '48px',
                      height: '48px',
                      objectFit: 'cover',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      border: '1px solid #dee2e6',
                      transition: 'transform 0.2s ease'
                    }}
                    onMouseEnter={(e) => e.target.style.transform = 'scale(1.1)'}
                    onMouseLeave={(e) => e.target.style.transform = 'scale(1)'}
                  />
                </div>

                {/* User Details */}
                <div>
                  <div style={{
                    fontSize: '14px',
                    fontWeight: '600',
                    color: '#212529',
                    marginBottom: '2px'
                  }}>
                    {photo.full_name}
                  </div>
                  <div style={{
                    fontSize: '13px',
                    color: '#6c757d'
                  }}>
                    {photo.email}
                  </div>
                  <div style={{
                    fontSize: '12px',
                    color: '#adb5bd',
                    marginTop: '2px'
                  }}>
                    ID: {photo.user_id}
                  </div>
                </div>

                {/* Photo Type */}
                <div>
                  <span style={{
                    padding: '4px 8px',
                    borderRadius: '12px',
                    fontSize: '11px',
                    fontWeight: '500',
                    backgroundColor: photo.photo_type === 'profile' ? '#e3f2fd' :
                                   photo.photo_type === 'normal' ? '#f3e5f5' : '#fff3e0',
                    color: photo.photo_type === 'profile' ? '#1565c0' :
                           photo.photo_type === 'normal' ? '#7b1fa2' : '#ef6c00',
                    border: `1px solid ${photo.photo_type === 'profile' ? '#bbdefb' :
                                       photo.photo_type === 'normal' ? '#ce93d8' : '#ffcc80'}`
                  }}>
                    {getPhotoTypeLabel(photo.photo_type)}
                  </span>
                </div>

                {/* Upload Time */}
                <div style={{
                  fontSize: '13px',
                  color: '#6c757d'
                }}>
                  {formatDate(photo.time)}
                </div>

                {/* Actions */}
                <div style={{
                  display: 'flex',
                  gap: '8px',
                  justifyContent: 'center'
                }}>
                  <button
                    style={{
                      padding: '6px 12px',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: actionLoading ? 'not-allowed' : 'pointer',
                      fontSize: '12px',
                      fontWeight: '500',
                      background: '#28a745',
                      color: 'white',
                      transition: 'all 0.2s ease',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px'
                    }}
                    onClick={() => handleReview(photo.id, 'approve')}
                    disabled={actionLoading}
                    onMouseEnter={(e) => !actionLoading && (e.target.style.backgroundColor = '#218838')}
                    onMouseLeave={(e) => !actionLoading && (e.target.style.backgroundColor = '#28a745')}
                  >
                    ✓ Approve
                  </button>
                  <button
                    style={{
                      padding: '6px 12px',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: actionLoading ? 'not-allowed' : 'pointer',
                      fontSize: '12px',
                      fontWeight: '500',
                      background: '#dc3545',
                      color: 'white',
                      transition: 'all 0.2s ease',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px'
                    }}
                    onClick={() => setSelectedPhoto(photo)}
                    disabled={actionLoading}
                    onMouseEnter={(e) => !actionLoading && (e.target.style.backgroundColor = '#c82333')}
                    onMouseLeave={(e) => !actionLoading && (e.target.style.backgroundColor = '#dc3545')}
                  >
                    ✗ Reject
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Modal for photo review */}
      {selectedPhoto && (
        <div style={{
          position: 'fixed',
          top: '0',
          left: '0',
          right: '0',
          bottom: '0',
          background: 'rgba(0,0,0,0.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: '1000'
        }} onClick={() => setSelectedPhoto(null)}>
          <div style={{
            background: 'white',
            borderRadius: '12px',
            maxWidth: '800px',
            width: '95%',
            maxHeight: '90vh',
            overflow: 'hidden',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
          }} onClick={(e) => e.stopPropagation()}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '24px 32px',
              borderBottom: '1px solid #e9ecef',
              background: '#f8f9fa'
            }}>
              <div>
                <h3 style={{ margin: '0 0 4px 0', color: '#212529', fontSize: '20px' }}>
                  Photo Review
                </h3>
                <p style={{ margin: '0', color: '#6c757d', fontSize: '14px' }}>
                  Review and moderate user-uploaded content
                </p>
              </div>
              <button style={{
                background: 'none',
                border: 'none',
                fontSize: '24px',
                cursor: 'pointer',
                color: '#6c757d',
                padding: '4px',
                borderRadius: '4px',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => e.target.style.backgroundColor = '#e9ecef'}
              onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
              onClick={() => setSelectedPhoto(null)}>×</button>
            </div>

            <div style={{
              display: 'flex',
              minHeight: '500px'
            }}>
              {/* Left side - Photo */}
              <div style={{
                flex: '1',
                padding: '32px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: '#f8f9fa',
                borderRight: '1px solid #e9ecef'
              }}>
                <img
                  src={getUploadUrl(selectedPhoto.path)}
                  alt={`${selectedPhoto.full_name}'s ${selectedPhoto.photo_type} photo`}
                  style={{
                    maxWidth: '100%',
                    maxHeight: '400px',
                    objectFit: 'contain',
                    borderRadius: '8px',
                    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
                  }}
                />
              </div>

              {/* Right side - Details and Actions */}
              <div style={{
                flex: '1',
                padding: '32px',
                display: 'flex',
                flexDirection: 'column'
              }}>
                {/* Photo Info */}
                <div style={{ marginBottom: '24px' }}>
                  <h4 style={{
                    margin: '0 0 16px 0',
                    color: '#212529',
                    fontSize: '18px'
                  }}>
                    Photo Information
                  </h4>

                  <div style={{ display: 'grid', gap: '12px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ fontWeight: '500', color: '#495057' }}>User:</span>
                      <span style={{ color: '#212529', fontWeight: '600' }}>{selectedPhoto.full_name}</span>
                    </div>

                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ fontWeight: '500', color: '#495057' }}>Email:</span>
                      <span style={{ color: '#212529' }}>{selectedPhoto.email}</span>
                    </div>

                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ fontWeight: '500', color: '#495057' }}>User ID:</span>
                      <span style={{ color: '#6c757d', fontFamily: 'monospace' }}>#{selectedPhoto.user_id}</span>
                    </div>

                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ fontWeight: '500', color: '#495057' }}>Photo Type:</span>
                      <span style={{
                        padding: '4px 12px',
                        borderRadius: '20px',
                        fontSize: '12px',
                        fontWeight: '500',
                        backgroundColor: selectedPhoto.photo_type === 'profile' ? '#e3f2fd' :
                                       selectedPhoto.photo_type === 'normal' ? '#f3e5f5' : '#fff3e0',
                        color: selectedPhoto.photo_type === 'profile' ? '#1565c0' :
                               selectedPhoto.photo_type === 'normal' ? '#7b1fa2' : '#ef6c00'
                      }}>
                        {getPhotoTypeLabel(selectedPhoto.photo_type)}
                      </span>
                    </div>

                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ fontWeight: '500', color: '#495057' }}>Uploaded:</span>
                      <span style={{ color: '#6c757d' }}>{formatDate(selectedPhoto.time)}</span>
                    </div>
                  </div>
                </div>

                {/* Rejection Reason */}
                <div style={{ marginBottom: '24px' }}>
                  <label htmlFor="review-notes" style={{
                    display: 'block',
                    marginBottom: '8px',
                    fontWeight: '600',
                    color: '#dc3545',
                    fontSize: '14px'
                  }}>
                    Rejection Reason (required for rejection)
                  </label>
                  <textarea
                    id="review-notes"
                    value={reviewNotes}
                    onChange={(e) => setReviewNotes(e.target.value)}
                    placeholder="Please provide a detailed reason for rejecting this photo..."
                    rows={4}
                    style={{
                      width: '100%',
                      padding: '12px',
                      border: '1px solid #dee2e6',
                      borderRadius: '6px',
                      fontFamily: 'inherit',
                      resize: 'vertical',
                      fontSize: '14px',
                      transition: 'border-color 0.15s ease',
                      outline: 'none'
                    }}
                    onFocus={(e) => e.target.style.borderColor = '#80bdff'}
                    onBlur={(e) => e.target.style.borderColor = '#dee2e6'}
                  />
                </div>
              </div>
            </div>

            <div style={{
              padding: '24px 32px',
              borderTop: '1px solid #e9ecef',
              background: '#f8f9fa',
              display: 'flex',
              gap: '12px',
              justifyContent: 'flex-end'
            }}>
              <button
                style={{
                  padding: '10px 24px',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: actionLoading ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                  background: '#6c757d',
                  color: 'white',
                  transition: 'all 0.2s ease',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}
                onClick={() => setSelectedPhoto(null)}
                disabled={actionLoading}
                onMouseEnter={(e) => !actionLoading && (e.target.style.backgroundColor = '#5a6268')}
                onMouseLeave={(e) => !actionLoading && (e.target.style.backgroundColor = '#6c757d')}
              >
                Cancel
              </button>
              <button
                style={{
                  padding: '10px 24px',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: actionLoading ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                  background: '#dc3545',
                  color: 'white',
                  transition: 'all 0.2s ease',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}
                onClick={() => handleReview(selectedPhoto.id, 'reject')}
                disabled={actionLoading || !reviewNotes.trim()}
                onMouseEnter={(e) => !actionLoading && !(!reviewNotes.trim()) && (e.target.style.backgroundColor = '#c82333')}
                onMouseLeave={(e) => !actionLoading && !(!reviewNotes.trim()) && (e.target.style.backgroundColor = '#dc3545')}
              >
                <span>✗</span>
                {actionLoading ? 'Processing...' : 'Reject Photo'}
              </button>
              <button
                style={{
                  padding: '10px 24px',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: actionLoading ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                  background: '#28a745',
                  color: 'white',
                  transition: 'all 0.2s ease',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}
                onClick={() => handleReview(selectedPhoto.id, 'approve')}
                disabled={actionLoading}
                onMouseEnter={(e) => !actionLoading && (e.target.style.backgroundColor = '#218838')}
                onMouseLeave={(e) => !actionLoading && (e.target.style.backgroundColor = '#28a745')}
              >
                <span>✓</span>
                {actionLoading ? 'Processing...' : 'Approve Photo'}
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default AdminPhotos;